import { FALLBACK_LOCALE, type SupportedLocale } from './supportedLocales.ts'

/**
 * One built image serves every deployment, so the product name and the language cannot be
 * baked into the shell at build time: `fiochat.ultra-mega-apps.com` and
 * `threadline.ultra-mega-apps.com` are the same artifact under two domains.
 *
 * The shell therefore ships with placeholders, and whoever serves it substitutes them for
 * the deployment it belongs to. The result is a first paint that is already in the right
 * language, without waiting for an API call.
 */
export const APPLICATION_SHELL_PLACEHOLDERS = {
  productName: '__PRODUCT_NAME__',
  productTagline: '__PRODUCT_TAGLINE__',
  documentLocale: '__DOCUMENT_LOCALE__',
  bootstrap: '__APPLICATION_BOOTSTRAP__',
} as const

export const APPLICATION_BOOTSTRAP_ELEMENT_ID = 'application-bootstrap'

export type ApplicationShellBranding = {
  productName: string
  productTagline: string
  /** The language this deployment serves, or `null` on a multilingual deployment. */
  deploymentLocale: SupportedLocale | null
}

/** The subset of the branding the running application reads back out of the shell. */
export type ApplicationBootstrap = {
  productName: string
  deploymentLocale: SupportedLocale | null
}

/**
 * A multilingual deployment still needs a language for the static shell, before any
 * browser preference has been read. It is the same fallback the rest of the system uses.
 */
export function resolveDocumentLocale(deploymentLocale: SupportedLocale | null): SupportedLocale {
  return deploymentLocale ?? FALLBACK_LOCALE
}

/**
 * Serializes the bootstrap for an inline `application/json` block. `<` is escaped so that
 * no localized value can ever close the surrounding script element.
 */
export function serializeApplicationBootstrap(bootstrap: ApplicationBootstrap): string {
  return JSON.stringify(bootstrap).replaceAll('<', '\\u003c')
}

export function parseApplicationBootstrap(serializedBootstrap: string): ApplicationBootstrap | null {
  let parsedBootstrap: unknown
  try {
    parsedBootstrap = JSON.parse(serializedBootstrap)
  } catch {
    return null
  }

  if (typeof parsedBootstrap !== 'object' || parsedBootstrap === null) {
    return null
  }

  const { productName, deploymentLocale } = parsedBootstrap as Record<string, unknown>
  if (typeof productName !== 'string') {
    return null
  }
  if (deploymentLocale !== null && typeof deploymentLocale !== 'string') {
    return null
  }

  return {
    productName,
    deploymentLocale: (deploymentLocale as SupportedLocale | null) ?? null,
  }
}

/** Substitutes every placeholder in the shell template for this deployment's branding. */
export function renderApplicationShellHtml(
  shellTemplateHtml: string,
  branding: ApplicationShellBranding,
): string {
  const serializedBootstrap = serializeApplicationBootstrap({
    productName: branding.productName,
    deploymentLocale: branding.deploymentLocale,
  })

  return shellTemplateHtml
    .replaceAll(APPLICATION_SHELL_PLACEHOLDERS.productName, escapeForHtml(branding.productName))
    .replaceAll(
      APPLICATION_SHELL_PLACEHOLDERS.productTagline,
      escapeForHtml(branding.productTagline),
    )
    .replaceAll(
      APPLICATION_SHELL_PLACEHOLDERS.documentLocale,
      resolveDocumentLocale(branding.deploymentLocale),
    )
    .replaceAll(APPLICATION_SHELL_PLACEHOLDERS.bootstrap, serializedBootstrap)
}

/** The manifest is branded from the same values, so an installed app matches its origin. */
export function applyBrandingToWebManifest(
  manifestContents: Record<string, unknown>,
  branding: ApplicationShellBranding,
): Record<string, unknown> {
  return {
    ...manifestContents,
    name: branding.productName,
    short_name: branding.productName,
    description: branding.productTagline,
    lang: resolveDocumentLocale(branding.deploymentLocale),
  }
}

/** Placeholders land in attribute values and in element text, so both are escaped. */
function escapeForHtml(rawText: string): string {
  return rawText
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
}
